import { type Biome } from '@goodtime/shared';
import { BiomeMidEffects } from './effects/BiomeMidEffects';

const MG_MODULES = import.meta.glob<{ default: string }>(
  '../../../assets/map_mg/*.png',
  { eager: true, query: '?url' },
);
const MG_BY_BIOME: Partial<Record<Biome['id'], string>> = Object.fromEntries(
  Object.entries(MG_MODULES)
    .map(([path, mod]) => {
      const id = path.match(/([^/]+)\.png$/)?.[1] ?? null;
      return id ? [id, mod.default] : null;
    })
    .filter((entry): entry is [string, string] => entry !== null),
);

/** Natural pixel size of every panorama PNG. Effects use this aspect
 * ratio so positions expressed as percentages are stable across viewport
 * heights. */
const PANORAMA_ASPECT = '2172 / 724';

export interface MapLayerMidProps {
  biome: Biome;
  scrollLeft: number;
  parallax: number;
}

/**
 * Map V2 — Layer 2 (mid-ground).
 *
 * <p>Renders the wide horizontal {@code map_mg/<biomeId>.png} panorama
 * with TRANSPARENT SKY so it composites on top of the L3 background.
 * Translates with the foreground scroll at the {@code parallax}
 * fraction (typically 0.5).
 *
 * <p>The image fills the full viewport height with its natural
 * aspect ratio preserved (width: auto). It anchors to the bottom of
 * the band so the painted ground line lands flush with the L1 grass
 * strip, and the parallax {@code left: offset} pans it horizontally
 * as the foreground scrolls. The L3 sky shows through everywhere the
 * source image is alpha-transparent.
 *
 * <p>If no panorama exists for the biome, renders nothing (the
 * background L3 sky+silhouettes show through).
 */
export function MapLayerMid({
  biome,
  scrollLeft,
  parallax,
}: MapLayerMidProps) {
  const mgUrl = MG_BY_BIOME[biome.id];
  if (!mgUrl) return null;
  const offset = -scrollLeft * parallax;
  // Wrapper takes the parallax offset; the image fills it 100%/100%
  // and per-biome effect children position themselves as a percentage
  // of the panorama's natural 2172×724 frame. Anything inside the
  // wrapper scrolls with L2 — this is the anchoring guarantee.
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 1,
      }}
      aria-hidden
    >
      <div
        style={{
          position: 'absolute',
          left: offset,
          bottom: 0,
          height: '100%',
          aspectRatio: PANORAMA_ASPECT,
          display: 'block',
        }}
      >
        <img
          src={mgUrl}
          alt=""
          draggable={false}
          // U12 L2 (2026-05-31) — fade to 0 on 404 so the L3 sky
          // gradient carries the scene. Same shape as the L1
          // BiomeListScreen fallback.
          onError={(e) => { e.currentTarget.style.opacity = '0'; }}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        />
        <BiomeMidEffects biomeId={biome.id} />
      </div>
    </div>
  );
}
